// In Results.tsx

import React, { useMemo, useState } from 'react';
import { symbols } from '../../../enum/symbols';
// import { useSelector } from 'react-redux'; // useAppSelector is preferred
import { Grid, Typography } from '@mui/material';
import { RootState } from '../../../../reduxService'; // Adatta
import { useTranslation } from 'react-i18next';
import TireAndLayers, { ILayerData, IGeogridInfo } from './TireAndLayers'; // Importa Tipi
import { unit as unitEnum, EProjectType } from '../../../enum/project'; // Adatta, Import EProjectType
// import { selectDynamicProject } from '../../../Dynamic/store/project/projectslice'; // No longer needed directly if using dynamicProject from RootState
import CustomTable from '../../../components/Layout/CustomTable/CustomTable';
// import { getActionByProjectType } from '../../../actionMap';
// import { useParams } from 'react-router-dom';
// import { selectProjectInfo } from '../../../store/projectInfo/projectInfoSlice'; // No longer needed if projectInfo comes from dynamicProject
import { useAppSelector } from '../../../../reduxService/hooks';
import './Results.scss';
// Use IContainerYardData as suggested by error, and ensure IDynamicProjectOutputData is correctly defined in IApiResponse.ts
import { IContainerYardData, IDynamicProjectOutputData, IParkingDeckData, IRailwayData } from '../../../api/IApiResponse'; // Added IParkingDeckData for parking deck project type
import { convertFieldValue } from '../../../services/UnitConversionService'; // Import convertFieldValue
import { getBallastLabel } from '../../../services/BallastCbrDatabase';
import { getSubgradeSoilTypeLabel } from '../../../services/ProjectTypeService';
import { IRailwayOutputData } from '../../../services/RailwayCalculationOrchestrator';
import { IRailwayDataWithGeogrid, IRailwayIterationData, IRailwayGraphicData } from '../../../utils/railwayTypes';

const Results = () => {
    const rawOutput = useAppSelector((state: RootState) => state.Dynamic.results.data?.outputData as IDynamicProjectOutputData | undefined);
    // const projectInfo = useAppSelector(selectProjectInfo); // Sourced from dynamicProject now
    const dynamicProject = useAppSelector((state: RootState) => state.Dynamic.project); // Get the whole dynamicProject state
    const { t } = useTranslation();

    const [unreinforcedStageHeight, setUnreinforcedStageHeight] = useState<number | undefined>(undefined);
    const [unreinforcedSubgradeY, setUnreinforcedSubgradeY] = useState<number | undefined>(undefined);

    const currentProjectType = dynamicProject.data?.type; // Get project type

    // Get the setStabilisedBallastThickness value from form fields
    const setStabilisedBallastThickness = useMemo(() => {
        if (dynamicProject.data?.type !== EProjectType.railways) return 0;

        // 🔧 FIX: Railway now uses FLAT structure (no .default wrapper)
        // REASON: Unified with other projects after transformProjectFromBackend()
        const railwayProjectInputsLocal = dynamicProject.data?.type === EProjectType.railways ? (dynamicProject.data?.projectInfo?.data as any) : null;
        const fromRailwayInputsDesignParams = railwayProjectInputsLocal?.designParameters?.setStabilisedBallastThickness;
        const fromRailwayInputsRoot = railwayProjectInputsLocal?.setStabilisedBallastThickness;

        // Try multiple possible locations for this value
        const fromFormFields = dynamicProject.data?.formFields?.setStabilisedBallastThickness as number;
        const fromFormFieldsNested = (dynamicProject.data?.formFields as any)?.designParameters?.setStabilisedBallastThickness as number;
        const fromProjectData = (dynamicProject.data?.projectInfo?.data as any)?.setStabilisedBallastThickness;
        const fromDesignParameters = (dynamicProject.data?.projectInfo?.data as any)?.designParameters?.setStabilisedBallastThickness;
        // 🔧 REMOVED: fromDefaultObject - Railway no longer uses .default wrapper

        // Return the first defined value found (including 0), prioritizing Input Parameters table source
        if (fromRailwayInputsDesignParams !== undefined && fromRailwayInputsDesignParams !== null) {
            return fromRailwayInputsDesignParams;
        }
        if (fromRailwayInputsRoot !== undefined && fromRailwayInputsRoot !== null) {
            return fromRailwayInputsRoot;
        }
        if (fromFormFields !== undefined && fromFormFields !== null) {
            return fromFormFields;
        }
        if (fromFormFieldsNested !== undefined && fromFormFieldsNested !== null) {
            return fromFormFieldsNested;
        }
        if (fromProjectData !== undefined && fromProjectData !== null) {
            return fromProjectData;
        }
        if (fromDesignParameters !== undefined && fromDesignParameters !== null) {
            return fromDesignParameters;
        }

        // Only use default value if no explicit value is set anywhere
        return 0.2; // Default value from ProjectTypeService
    }, [dynamicProject.data?.type, dynamicProject.data?.formFields?.setStabilisedBallastThickness, dynamicProject.data?.formFields, dynamicProject.data?.projectInfo?.data]);

    const processedOutput = useMemo(() => {
        if (!rawOutput || !dynamicProject.data) return rawOutput;

        const projectUnit = dynamicProject?.data?.unit;
        if (projectUnit !== unitEnum.Imperial) {
            return rawOutput;
        }

        const newOutput = JSON.parse(JSON.stringify(rawOutput)) as IDynamicProjectOutputData; // Deep clone

        if (newOutput.deckDesign) {
            const dd = newOutput.deckDesign;
            // Use imported convertFieldValue directly
            dd.unreinforcedHeight =
                convertFieldValue(
                    { name: 'deckDesign.unreinforcedHeight', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.unreinforcedHeight,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'output.deckDesign.unreinforcedHeight'
                ) ?? dd.unreinforcedHeight;
            dd.reinforcedHeight =
                convertFieldValue(
                    { name: 'deckDesign.reinforcedHeight', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.reinforcedHeight,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'output.deckDesign.reinforcedHeight'
                ) ?? dd.reinforcedHeight;
            dd.thicknessSaving =
                convertFieldValue(
                    { name: 'deckDesign.thicknessSaving', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.thicknessSaving,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'output.deckDesign.thicknessSaving'
                ) ?? dd.thicknessSaving;

            if (dd.unreinforcedDeckAnalysisData) {
                dd.unreinforcedDeckAnalysisData.forEach((item, index) => {
                    item.h =
                        convertFieldValue(
                            { name: `deckDesign.unreinforcedDeckAnalysisData[${index}].h`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.h,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.unreinforcedDeckAnalysisData[${index}].h`
                        ) ?? item.h;
                });
            }
            if (dd.reinforcedDeckAnalysisData) {
                dd.reinforcedDeckAnalysisData.forEach((item, index) => {
                    item.h =
                        convertFieldValue(
                            { name: `deckDesign.reinforcedDeckAnalysisData[${index}].h`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.h,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.reinforcedDeckAnalysisData[${index}].h`
                        ) ?? item.h;
                });
            }
            if (dd.cbrLookupTable) {
                dd.cbrLookupTable.forEach((item, index) => {
                    item.cuKPa =
                        convertFieldValue(
                            { name: `deckDesign.cbrLookupTable[${index}].cuKPa`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.cuKPa,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.cbrLookupTable[${index}].cuKPa`
                        ) ?? item.cuKPa;
                    item.hUnstabilisedM =
                        convertFieldValue(
                            { name: `deckDesign.cbrLookupTable[${index}].hUnstabilisedM`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.hUnstabilisedM,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.cbrLookupTable[${index}].hUnstabilisedM`
                        ) ?? item.hUnstabilisedM;
                    item.hStabilisedM =
                        convertFieldValue(
                            { name: `deckDesign.cbrLookupTable[${index}].hStabilisedM`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.hStabilisedM,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.cbrLookupTable[${index}].hStabilisedM`
                        ) ?? item.hStabilisedM;
                });
            }
            const ddWithIteration = dd as IRailwayIterationData;
            if (ddWithIteration.iterationTable && Array.isArray(ddWithIteration.iterationTable)) {
                ddWithIteration.iterationTable.forEach((item, index) => {
                    item.height =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].height`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.height,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].height`
                        ) ?? item.height;
                    item.stabBearingCapacity =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].stabBearingCapacity`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.stabBearingCapacity,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].stabBearingCapacity`
                        ) ?? item.stabBearingCapacity;
                    item.unstabBearingCapacity =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].unstabBearingCapacity`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.unstabBearingCapacity,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].unstabBearingCapacity`
                        ) ?? item.unstabBearingCapacity;
                    item.verticalPressure =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].verticalPressure`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.verticalPressure,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].verticalPressure`
                        ) ?? item.verticalPressure;
                });
            }
        }
        return newOutput;
    }, [rawOutput, dynamicProject.data?.unit, t]); // Added t to dependencies as it's used in mockFieldConfig indirectly if we were to translate field names (though not currently done here)

    // Get Railway calculation results from Redux (dispatched by RailwayCalculationOrchestrator)
    const railwayCalculationFromRedux = useMemo(() => {
        if (dynamicProject.data?.type !== EProjectType.railways) return null;

        // Try to get Railway results from Redux store
        const railwayOutputData = rawOutput as IRailwayOutputData;
        return railwayOutputData?.railwayCalculation || null;
    }, [rawOutput, dynamicProject.data?.type]);

    // Calculate Railway Dynamic Data using the dedicated service (fallback for direct access)
    const calculatedRailwayData = useMemo(() => {
        if (railwayCalculationFromRedux) {
            return railwayCalculationFromRedux;
        }
        // If no data available, return null instead of throwing error to allow fallback values in diagrams
        return null;
    }, [railwayCalculationFromRedux]);

    // Get backend response data from Redux (if backend calculation was used)
    const backendResponseData = useMemo(() => {
        // Check if we have backend response structure in rawOutput
        if (rawOutput && typeof rawOutput === 'object' && 'calculatedData' in rawOutput) {
            const result = rawOutput as {
                calculatedData?: {
                    title: string;
                    rows: Array<{
                        description: string;
                        symbol: string;
                        value: number;
                        unit: string;
                    }>;
                };
                trainAnalysis?: Array<{
                    description: string;
                    cargoSymbol: string;
                    cargoValue: number;
                    cargoUnit: string;
                    passengerSymbol: string;
                    passengerValue: number;
                    passengerUnit: string;
                }>;
                advancedBallastDesign?: any[];
                designSummary?: any[];
                calculatedAt?: string;
                debugInfo?: string;
            };

            return result;
        }
        return null;
    }, [rawOutput]);

    // Source inputs from dynamicProject.data.projectInfo.data
    const projectInputs = useMemo(() => {
        if (dynamicProject.data?.type === EProjectType.container_yard) {
            return dynamicProject.data.projectInfo?.data as IContainerYardData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.parking_deck) {
            return dynamicProject.data.projectInfo?.data as IParkingDeckData | undefined;
        }
        return undefined;
    }, [dynamicProject.data]);

    // --- Get selected geogrid product name for dynamic labeling ---
    const selectedGeogridProduct = useMemo(() => {
        if (currentProjectType !== EProjectType.railways) return null;
        const railwayData = dynamicProject.data?.projectInfo?.data as IRailwayData;
        if (!railwayData) return null;

        // For Railway projects, try to get the selected geogrid product from various possible locations
        // Check if there's a geogrid field in the form data
        const railwayDataWithGeogrid = railwayData as IRailwayDataWithGeogrid;
        const selectedGeogrid = railwayDataWithGeogrid?.geoGrid?.selectedGeogrid || railwayDataWithGeogrid?.selectedGeogrid || railwayDataWithGeogrid?.selected_geogrid_product;

        // Debug logging

        const result = selectedGeogrid && selectedGeogrid.trim() !== '' && selectedGeogrid !== 'none_selected' ? selectedGeogrid : null;

        return result;
    }, [currentProjectType, dynamicProject.data?.projectInfo?.data]);

    // === INPUT TABLES ===
    const unitSystem = dynamicProject.data?.unit || 'Metric';
    // 🔧 FIX: Railway now uses FLAT structure (no .default wrapper)
    // REASON: Unified with other projects after transformProjectFromBackend()
    const railwayProjectInputs = dynamicProject?.data?.type === EProjectType.railways ? (dynamicProject?.data?.projectInfo?.data as any) : null;
    const railwayToDesign = railwayProjectInputs?.railwayType?.railwayToDesign;

    // Helper to get units
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === 'Metric' ? metricUnit : imperialUnit;
    };

    // Helper for consistent conversion and rounding for input tables
    const convertInputValue = (value: number | undefined, fieldName: string, metricUnit: string, imperialUnit: string): string => {
        if (typeof value !== 'number') return 'N/A';
        if (unitSystem === unitEnum.Imperial) {
            const converted = convertFieldValue(
                { name: fieldName, type: 'number', unit: [metricUnit, imperialUnit], isConvertible: true },
                value,
                unitEnum.SI,
                unitEnum.Imperial,
                `debug.${fieldName}`
            );
            return converted !== null ? converted.toFixed(3) : value.toString();
        }
        return value.toString();
    };

    // Helper for converting OUTPUT values (from backend) to Imperial
    const convertOutputValue = (value: number | undefined | null, metricUnit: string, decimals = 3): { value: string; unit: string } => {
        if (value === undefined || value === null) return { value: 'N/A', unit: metricUnit };

        if (unitSystem !== unitEnum.Imperial) {
            return { value: value.toFixed(decimals), unit: metricUnit };
        }

        // Map metric units to imperial equivalents
        const unitMappings: Record<string, { imperialUnit: string; conversionFactor: number }> = {
            m: { imperialUnit: 'ft', conversionFactor: 3.28084 }, // m → ft
            kN: { imperialUnit: 'lbf', conversionFactor: 224.809 }, // kN → lbf
            kPa: { imperialUnit: 'psi', conversionFactor: 0.145038 }, // kPa → psi
            mm: { imperialUnit: 'in', conversionFactor: 0.0393701 }, // mm → in
        };

        const mapping = unitMappings[metricUnit];
        if (mapping) {
            const convertedValue = value * mapping.conversionFactor;
            return { value: convertedValue.toFixed(decimals), unit: mapping.imperialUnit };
        }

        // No conversion available, return original
        return { value: value.toFixed(decimals), unit: metricUnit };
    };

    // Helper to render symbol strings with proper subscripts (e.g., "h'bu" → h'<sub>bu</sub>)
    const renderSymbolWithSubscript = (symbol: string | undefined): React.ReactNode => {
        if (!symbol || symbol === 'N/A') return symbol || 'N/A';

        // Define patterns for Railway symbols that need subscript formatting
        // Format: [pattern to match, main text, subscript text]
        const symbolPatterns: Array<{ pattern: RegExp; render: (match: RegExpMatchArray) => React.ReactNode }> = [
            // Ballast thickness symbols: h'bu, h'bs, hbu, hbs
            {
                pattern: /^h'([a-z]+)$/i,
                render: m => (
                    <span>
                        h'<sub>{m[1]}</sub>
                    </span>
                ),
            },
            {
                pattern: /^h([a-z]{2,})$/i,
                render: m => (
                    <span>
                        h<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // Deformation symbols: d'vu, d'vs, dvu, dvs
            {
                pattern: /^d'([a-z]+)$/i,
                render: m => (
                    <span>
                        d'<sub>{m[1]}</sub>
                    </span>
                ),
            },
            {
                pattern: /^d([a-z]{2,})$/i,
                render: m => (
                    <span>
                        d<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // Rut depth: hu, hs
            {
                pattern: /^h([us])$/i,
                render: m => (
                    <span>
                        h<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // Rut depth: ru, rs
            {
                pattern: /^r([us])$/i,
                render: m => (
                    <span>
                        r<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // Pressure/force symbols: Pc, Pp, Pdyn, σsb
            {
                pattern: /^P([a-z]+)$/i,
                render: m => (
                    <span>
                        P<sub>{m[1]}</sub>
                    </span>
                ),
            },
            {
                pattern: /^σ([a-z]+)$/i,
                render: m => (
                    <span>
                        σ<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // CBR symbols: CBRb, CBRsb, CBRsg
            {
                pattern: /^CBR([a-z]+)$/i,
                render: m => (
                    <span>
                        CBR<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // Poisson ratio: μb, μsb, μsg
            {
                pattern: /^μ([a-z]+)$/i,
                render: m => (
                    <span>
                        μ<sub>{m[1]}</sub>
                    </span>
                ),
            },
            // Number of passages: N
            { pattern: /^N$/i, render: () => <span>N</span> },
        ];

        for (const { pattern, render } of symbolPatterns) {
            const match = symbol.match(pattern);
            if (match) {
                return render(match);
            }
        }

        // Return original symbol if no pattern matches
        return symbol;
    };

    // Prepare Cargo Train Inputs table data
    const cargoTrainInputs = useMemo(() => {
        const inputs = [];
        // Support both nested (loadData.field) and flat (field) data structures
        // REASON: Backend returns nested, but Redux form may store flat after transformProjectFromBackend()
        const loadData = railwayProjectInputs?.loadData || railwayProjectInputs;
        const trafficData = railwayProjectInputs?.trafficData || railwayProjectInputs;

        if (railwayProjectInputs) {
            if (railwayToDesign === 'cargo_train') {
                // For cargo_train, use standard field names
                inputs.push(['Design axle load', 'W', convertInputValue(loadData?.designAxleLoad, 'designAxleLoad', 'kN', 'lbf'), getUnit('kN', 'lbf')]);
                inputs.push([
                    'Train speed',
                    <span>
                        N<sub>p</sub>
                    </span>,
                    convertInputValue(loadData?.trainSpeed, 'trainSpeed', 'km/h', 'mph'),
                    getUnit('km/h', 'mph'),
                ]);
                inputs.push([
                    'Wheel diameter',
                    <span>D<sub>w</sub></span>,
                    convertInputValue(loadData?.wheelDiameter, 'wheelDiameter', 'm', 'ft'),
                    getUnit('m', 'ft'),
                ]);
                inputs.push([
                    'Number of trains per day',
                    <span>
                        N<sub>p</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfTrainsPerDay, 'numberOfTrainsPerDay', '-', '-'),
                    '-',
                ]);
                inputs.push(['Number of cars per train', 'W', convertInputValue(trafficData?.numberOfCarsPerTrain, 'numberOfCarsPerTrain', '-', '-'), '-']);
                inputs.push(['Number of axes per car', 'a1', convertInputValue(trafficData?.numberOfAxisPerCar, 'numberOfAxisPerCar', '-', '-'), '-']);
                inputs.push([
                    'Design life',
                    <span>
                        P<sub>t</sub>
                    </span>,
                    convertInputValue(trafficData?.designLife, 'designLife', 'years', 'years'),
                    'years',
                ]);
            } else if (railwayToDesign === 'mixed_train') {
                // For mixed_train, use cargo-specific field names
                inputs.push(['Design axle load', 'W', convertInputValue(loadData?.designAxleLoadCargo, 'designAxleLoadCargo', 'kN', 'lbf'), getUnit('kN', 'lbf')]);
                inputs.push(['Train speed', 'C', convertInputValue(loadData?.trainSpeedCargo, 'trainSpeedCargo', 'km/h', 'mph'), getUnit('km/h', 'mph')]);
                inputs.push(['Wheel diameter', <span>D<sub>w</sub></span>, convertInputValue(loadData?.wheelDiameterCargo, 'wheelDiameterCargo', 'm', 'ft'), getUnit('m', 'ft')]);
                inputs.push(['Number of trains per day', <span>N<sub>t</sub></span>, convertInputValue(trafficData?.numberOfTrainsPerDayCargo, 'numberOfTrainsPerDayCargo', '-', '-'), '-']);
                inputs.push(['Number of cars per train', <span>N<sub>w</sub></span>, convertInputValue(trafficData?.numberOfCarsPerTrainCargo, 'numberOfCarsPerTrainCargo', '-', '-'), '-']);
                inputs.push(['Number of axes per car', <span>N<sub>a</sub></span>, convertInputValue(trafficData?.numberOfAxisPerCarCargo, 'numberOfAxisPerCarCargo', '-', '-'), '-']);
                inputs.push(['Design life', 'T', convertInputValue(trafficData?.designLifeCargo, 'designLifeCargo', 'years', 'years'), 'years']);
            }
        }

        return inputs;
    }, [railwayProjectInputs, railwayToDesign, t, getUnit, convertInputValue]);

    // Prepare Passenger Train Inputs table data
    const passengerTrainInputs = useMemo(() => {
        const inputs = [];
        // Support both nested (loadData.field) and flat (field) data structures
        // REASON: Backend returns nested, but Redux form may store flat after transformProjectFromBackend()
        const loadData = railwayProjectInputs?.loadData || railwayProjectInputs;
        const trafficData = railwayProjectInputs?.trafficData || railwayProjectInputs;

        if (railwayProjectInputs) {
            if (railwayToDesign === 'passenger_train') {
                // For passenger_train, use standard field names
                inputs.push(['Design axle load', 'W', convertInputValue(loadData?.designAxleLoad, 'designAxleLoad', 'kN', 'lbf'), getUnit('kN', 'lbf')]);
                inputs.push(['Train speed', 'C', convertInputValue(loadData?.trainSpeed, 'trainSpeed', 'km/h', 'mph'), getUnit('km/h', 'mph')]);
                inputs.push([
                    'Wheel diameter',
                    <span>
                        D<sub>w</sub>
                    </span>,
                    convertInputValue(loadData?.wheelDiameter, 'wheelDiameter', 'm', 'ft'),
                    getUnit('m', 'ft'),
                ]);
                inputs.push([
                    'Number of trains per day',
                    <span>
                        N<sub>t</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfTrainsPerDay, 'numberOfTrainsPerDay', '-', '-'),
                    '-',
                ]);
                inputs.push([
                    'Number of cars per train',
                    <span>
                        N<sub>w</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfCarsPerTrain, 'numberOfCarsPerTrain', '-', '-'),
                    '-',
                ]);
                inputs.push([
                    'Number of axes per car',
                    <span>
                        N<sub>a</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfAxisPerCar, 'numberOfAxisPerCar', '-', '-'),
                    '-',
                ]);
                inputs.push(['Design life', 'T', convertInputValue(trafficData?.designLife, 'designLife', 'years', 'years'), 'years']);
            } else if (railwayToDesign === 'mixed_train') {
                // For mixed_train, use passenger-specific field names
                inputs.push(['Design axle load', 'W', convertInputValue(loadData?.designAxleLoadPassenger, 'designAxleLoadPassenger', 'kN', 'lbf'), getUnit('kN', 'lbf')]);
                inputs.push(['Train speed', 'C', convertInputValue(loadData?.trainSpeedPassenger, 'trainSpeedPassenger', 'km/h', 'mph'), getUnit('km/h', 'mph')]);
                inputs.push([
                    'Wheel diameter',
                    <span>
                        D<sub>w</sub>
                    </span>,
                    convertInputValue(loadData?.wheelDiameterPassenger, 'wheelDiameterPassenger', 'm', 'ft'),
                    getUnit('m', 'ft'),
                ]);
                inputs.push([
                    'Number of trains per day',
                    <span>
                        N<sub>t</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfTrainsPerDayPassenger, 'numberOfTrainsPerDayPassenger', '-', '-'),
                    '-',
                ]);
                inputs.push([
                    'Number of cars per train',
                    <span>
                        N<sub>w</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfCarsPerTrainPassenger, 'numberOfCarsPerTrainPassenger', '-', '-'),
                    '-',
                ]);
                inputs.push([
                    'Number of axes per car',
                    <span>
                        N<sub>a</sub>
                    </span>,
                    convertInputValue(trafficData?.numberOfAxisPerCarPassenger, 'numberOfAxisPerCarPassenger', '-', '-'),
                    '-',
                ]);
                inputs.push(['Design life', 'T', convertInputValue(trafficData?.designLifePassenger, 'designLifePassenger', 'years', 'years'), 'years']);
            }
        }

        return inputs;
    }, [railwayProjectInputs, railwayToDesign, t, getUnit, convertInputValue]);

    // Prepare Railway Geometry table data
    const railwayGeometryInputs = useMemo(() => {
        const inputs = [];
        const trackGeometry = railwayProjectInputs?.trackGeometry;

        if (railwayProjectInputs && trackGeometry) {
            inputs.push([
                'Rail width',
                <span>
                    B<sub>r</sub>
                </span>,
                convertInputValue(trackGeometry.railWidth, 'railWidth', 'm', 'ft'),
                getUnit('m', 'ft'),
            ]);
            inputs.push([
                'Sleeper thickness',
                <span>
                    T<sub>s</sub>
                </span>,
                convertInputValue(trackGeometry.sleeperThickness, 'sleeperThickness', 'm', 'ft'),
                getUnit('m', 'ft'),
            ]);
            inputs.push([
                'Sleeper width',
                <span>
                    B<sub>s</sub>
                </span>,
                convertInputValue(trackGeometry.sleeperWidth, 'sleeperWidth', 'm', 'ft'),
                getUnit('m', 'ft'),
            ]);
            inputs.push([
                'Load spreading angle in sleepers',
                <span>
                    α<sub>s</sub>
                </span>,
                convertInputValue(trackGeometry.loadSpreadingAngle, 'loadSpreadingAngle', '°', '°'),
                '°',
            ]);

            // Add calculated geometry values from backend or local Railway service
            let equivalentLength: number | undefined;
            let widthLoadedArea: number | undefined;

            // Try to get from backend response first
            if (backendResponseData?.calculatedData?.rows) {
                const equivalentLengthRow = backendResponseData.calculatedData.rows.find(row => row.symbol === 'L' || row.description?.includes('Equivalent length'));
                const widthLoadedAreaRow = backendResponseData.calculatedData.rows.find(row => row.symbol === 'bL' || row.description?.includes('Width of loaded area'));
                equivalentLength = equivalentLengthRow?.value;
                widthLoadedArea = widthLoadedAreaRow?.value;
            }
            // Fallback to local calculation if available
            else if (calculatedRailwayData) {
                equivalentLength = calculatedRailwayData.equivalentLength;
                widthLoadedArea = calculatedRailwayData.widthLoadedArea;
            }

            inputs.push(['Equivalent length of loaded area on top of ballast', 'L', convertInputValue(equivalentLength, 'equivalentLength', 'm', 'ft'), getUnit('m', 'ft')]);
            inputs.push(['Width of loaded area on top of ballast', 'bL', convertInputValue(widthLoadedArea, 'widthLoadedArea', 'm', 'ft'), getUnit('m', 'ft')]);
        }

        return inputs;
    }, [railwayProjectInputs, calculatedRailwayData, backendResponseData, t, getUnit, convertInputValue]);

    // Prepare Soil Properties table data
    const soilPropertiesInputs = useMemo(() => {
        const inputs = [];
        const soilProperties = railwayProjectInputs?.soilProperties;
        const poissonRatios = railwayProjectInputs?.poissonRatios;

        if (railwayProjectInputs && soilProperties) {
            // Add thickness of soft subgrade
            if (soilProperties.thicknessOfSoftSubgrade !== undefined) {
                inputs.push([
                    'Thickness of soft subgrade',
                    <span>
                        H<sub>s</sub>
                    </span>,
                    convertInputValue(soilProperties.thicknessOfSoftSubgrade, 'thicknessOfSoftSubgrade', 'm', 'ft'),
                    getUnit('m', 'ft'),
                ]);
            }

            // Add CBR values
            if (soilProperties.cbrOfBallast !== undefined) {
                inputs.push([
                    'CBR of ballast',
                    <span>
                        CBR<sub>b</sub>
                    </span>,
                    convertInputValue(soilProperties.cbrOfBallast, 'cbrOfBallast', '-', '-'),
                    '-',
                ]);
            }
            if (soilProperties.cbrOfSubballast !== undefined) {
                inputs.push([
                    'CBR of subballast',
                    <span>
                        CBR<sub>sb</sub>
                    </span>,
                    convertInputValue(soilProperties.cbrOfSubballast, 'cbrOfSubballast', '-', '-'),
                    '-',
                ]);
            }
            if (soilProperties.cbrOfSubgrade !== undefined) {
                inputs.push([
                    'CBR of subgrade',
                    <span>
                        CBR<sub>sg</sub>
                    </span>,
                    convertInputValue(soilProperties.cbrOfSubgrade, 'cbrOfSubgrade', '-', '-'),
                    '-',
                ]);
            }

            // Add Poisson ratios from poissonRatios section
            if (poissonRatios) {
                if (poissonRatios.poissonRatioOfBallast !== undefined) {
                    inputs.push([
                        'Poisson ratio of ballast',
                        <span>
                            μ<sub>b</sub>
                        </span>,
                        convertInputValue(poissonRatios.poissonRatioOfBallast, 'poissonRatioOfBallast', '-', '-'),
                        '-',
                    ]);
                }
                if (poissonRatios.poissonRatioOfSubballast !== undefined) {
                    inputs.push([
                        'Poisson ratio of subballast',
                        <span>
                            μ<sub>sb</sub>
                        </span>,
                        convertInputValue(poissonRatios.poissonRatioOfSubballast, 'poissonRatioOfSubballast', '-', '-'),
                        '-',
                    ]);
                }
                if (poissonRatios.poissonRatioOfSubgrade !== undefined) {
                    inputs.push([
                        'Poisson ratio of subgrade',
                        <span>
                            μ<sub>sg</sub>
                        </span>,
                        convertInputValue(poissonRatios.poissonRatioOfSubgrade, 'poissonRatioOfSubgrade', '-', '-'),
                        '-',
                    ]);
                }
            }

            // Add material types
            const materialTypes: IRailwayData['materialTypes'] = railwayProjectInputs.materialTypes;
            if (materialTypes?.ballastType) {
                inputs.push(['Ballast type', '-', getBallastLabel(materialTypes.ballastType), '-']);
            }
            if (materialTypes?.subgradeSoilType) {
                inputs.push(['Subgrade soil type', '-', getSubgradeSoilTypeLabel(materialTypes.subgradeSoilType), '-']);
            }
        }

        return inputs;
    }, [railwayProjectInputs, calculatedRailwayData, t, getUnit, convertInputValue]);

    // Helper function to render input table using CustomTable with PDF-matching styles
    const renderInputTable = (tableData: (string | React.ReactNode)[][], tableTitle: string, testId?: string) => {
        if (tableData.length === 0) {
            return null;
        }

        // Define table columns matching PDF structure
        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid container spacing={0} style={{ marginTop: '16px' }}>
                <Grid item xs={12} style={{ marginBottom: '8px' }}>
                    <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold' }}>
                        {tableTitle}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <CustomTable columns={columns} data={tableData} />
                </Grid>
            </Grid>
        );
    };

    // --- Prepara i dati per il diagramma NON RINFORZATO (Sinistra) ---
    const unreinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hu = processedOutput.deckDesign.unreinforcedHeight; // Always use unreinforcedHeight

        if (hu === undefined) {
            return null;
        }

        const surfaceThick = 0; // Set to 0 for railways as requested
        const baseThickUnreinforced = Math.max(0, hu - surfaceThick);
        const subgradeVisible = 0.5; // Altezza fissa disegno

        const surfaceLayer: ILayerData = { name: t('surface_layer'), thickness: surfaceThick, color: '#5A5A5A' };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickUnreinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        return { surfaceLayer, baseCourse, subgrade, rightDimensionValue: hu, unitLabel };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per il diagramma RINFORZATO (Destra) ---
    const reinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hr = processedOutput.deckDesign.reinforcedHeight; // Always use reinforcedHeight
        const currentSavingPercentage = processedOutput.deckDesign.thicknessSavingPercentage; // Always use thicknessSavingPercentage

        if (hr === undefined) {
            return null;
        }
        // Saving percentage can be undefined, especially if not applicable
        // The PDF component should handle undefined savingPercentage gracefully (e.g., not show the saving box).

        const surfaceThick = 0; // Set to 0 for railways as requested
        const baseThickReinforced = Math.max(0, hr - surfaceThick); // Base course is total reinforced height minus surface
        const subgradeVisible = 0.5;

        const firstBaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  positionFromBottomOfBase: firstBaseGeogrid.position ?? 0,
                  label: firstBaseGeogrid.selectedGeogrid,
              }
            : undefined;

        const surfaceLayer: ILayerData = { name: t('surface_layer'), thickness: surfaceThick, color: '#5A5A5A' };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickReinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        return { surfaceLayer, baseCourse, subgrade, rightDimensionValue: hr, savingPercentage: currentSavingPercentage, geogrid: geogridInfo, unitLabel };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per il diagramma RAILWAY NON RINFORZATO (Sinistra) ---
    const unreinforcedRailwayGraphicData = useMemo(() => {
        if (dynamicProject.data?.type !== EProjectType.railways) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const mToFtFactor = 3.28084; // Conversion factor m → ft

        let ballastThickness = 0.3; // default fallback (in meters)
        let subballastThickness = 0.0; // default fallback (in meters)

        // Usa dati della Design Summary dal backend se disponibili
        if (backendResponseData?.designSummary && backendResponseData.designSummary.length > 0) {
            // Cerca "Ballast thickness with subballast" per unstabilized (h'bu)
            const ballastWithSubballastRow = backendResponseData.designSummary.find(row => row.description?.includes('Ballast thickness with subballast') || row.description?.includes("h'bu"));
            if (ballastWithSubballastRow) {
                ballastThickness = ballastWithSubballastRow.unstabilizedValue ?? ballastThickness;
            }

            // Cerca "Subballast thickness" per unstabilized (hsbu)
            const subballastRow = backendResponseData.designSummary.find(row => row.description?.includes('Subballast thickness') || row.description?.includes('hsbu'));
            if (subballastRow) {
                subballastThickness = subballastRow.unstabilizedValue ?? subballastThickness;
            }
        } else if (calculatedRailwayData?.unstabilizedCalculations) {
            ballastThickness = calculatedRailwayData.unstabilizedCalculations.ballastThickness ?? ballastThickness;
            subballastThickness = calculatedRailwayData.unstabilizedCalculations.subballastThickness ?? subballastThickness;
        }

        // Keep metric values for drawing proportions, convert only for display labels
        const subgradeVisible = 1.0;
        const sleeperThickness = 0;

        // For display: convert to Imperial if needed
        const ballastThicknessDisplay = isImperial ? ballastThickness * mToFtFactor : ballastThickness;
        const subballastThicknessDisplay = isImperial ? subballastThickness * mToFtFactor : subballastThickness;
        const totalHeightDisplay = ballastThicknessDisplay + (subballastThicknessDisplay > 0 ? subballastThicknessDisplay : 0) + (isImperial ? subgradeVisible * mToFtFactor : subgradeVisible);

        // Layers use METRIC values for correct visual proportions
        // Guard: persisted pre-fix projects can carry a negative subballast (BE now clamps at source);
        // a negative layer thickness corrupts the drawing geometry, so clamp to 0 here too.
        const ballastLayer: ILayerData = { name: t('ballast', 'Ballast'), thickness: ballastThickness, color: '#4CAF50' };
        const subballastLayer: ILayerData = { name: t('subballast', 'Subballast'), thickness: Math.max(0, subballastThickness), color: '#FFC107' };
        const subgradeLayer: ILayerData = { name: t('subgrade', 'Subgrade'), thickness: subgradeVisible, color: '#8D6E63' };

        return {
            surfaceLayer: ballastLayer,
            baseCourse: subballastLayer,
            subgrade: subgradeLayer,
            rightDimensionValue: totalHeightDisplay, // Display value (converted)
            unitLabel,
            sleeperThickness,
            ballastThickness: ballastThicknessDisplay, // Display value (converted)
            subballastThickness: subballastThicknessDisplay, // Display value (converted)
        };
    }, [dynamicProject.data?.type, calculatedRailwayData, backendResponseData, t, dynamicProject.data?.unit]);

    // --- Prepara i dati per il diagramma RAILWAY RINFORZATO (Destra) ---
    const reinforcedRailwayGraphicData = useMemo(() => {
        if (dynamicProject.data?.type !== EProjectType.railways) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const mToFtFactor = 3.28084; // Conversion factor m → ft

        let ballastThickness = 0.25; // default fallback (in meters)
        let subballastThickness = 0.0; // default fallback (in meters)

        // Usa dati della Design Summary dal backend se disponibili
        if (backendResponseData?.designSummary && backendResponseData.designSummary.length > 0) {
            // Cerca "Ballast thickness with subballast" per stabilized (h'bs)
            const ballastWithSubballastRow = backendResponseData.designSummary.find(row => row.description?.includes('Ballast thickness with subballast') || row.description?.includes("h'bs"));
            if (ballastWithSubballastRow) {
                ballastThickness = ballastWithSubballastRow.stabilizedValue ?? ballastThickness;
            }

            // Cerca "Subballast thickness" per stabilized (hsbs)
            const subballastRow = backendResponseData.designSummary.find(row => row.description?.includes('Subballast thickness') || row.description?.includes('hsbs'));
            if (subballastRow) {
                subballastThickness = subballastRow.stabilizedValue ?? subballastThickness;
            }
        } else if (calculatedRailwayData?.stabilizedCalculations) {
            ballastThickness = calculatedRailwayData.stabilizedCalculations.ballastThickness ?? ballastThickness;
            subballastThickness = calculatedRailwayData.stabilizedCalculations.subballastThickness ?? subballastThickness;
        }

        // Keep metric values for drawing proportions, convert only for display labels
        const subgradeVisible = 1.0;
        const sleeperThickness = 0;

        // For display: convert to Imperial if needed
        const ballastThicknessDisplay = isImperial ? ballastThickness * mToFtFactor : ballastThickness;
        const subballastThicknessDisplay = isImperial ? subballastThickness * mToFtFactor : subballastThickness;
        // Usa savingPercentage direttamente dal backend se disponibile
        let savingPercentage: number | undefined;

        if (backendResponseData?.designSummary && backendResponseData.designSummary.length > 0) {
            // Cerca "Thickness saving percentage" nel designSummary del backend
            const thicknessSavingRow = backendResponseData.designSummary.find(row => row.description?.includes('Thickness saving percentage') || row.stabilizedSymbol === 'setPercent');

            if (thicknessSavingRow) {
                // Usa il valore unstabilized per il thickness saving percentage
                savingPercentage = thicknessSavingRow.unstabilizedValue;
            }
        }

        // Fallback: calcola manualmente solo se il backend non fornisce il valore
        if (savingPercentage === undefined) {
            let unstabilizedThickness: number | undefined;

            if (backendResponseData?.designSummary && backendResponseData.designSummary.length > 0) {
                const ballastWithSubballastRow = backendResponseData.designSummary.find(row => row.description?.includes('Ballast thickness with subballast'));
                if (ballastWithSubballastRow) {
                    unstabilizedThickness = ballastWithSubballastRow.unstabilizedValue;
                }
            } else if (calculatedRailwayData?.unstabilizedCalculations) {
                unstabilizedThickness = calculatedRailwayData.unstabilizedCalculations.ballastThickness;
            }

            if (unstabilizedThickness && ballastThickness) {
                savingPercentage = ((unstabilizedThickness - ballastThickness) / unstabilizedThickness) * 100;
            }
        }

        // Layers use METRIC values for correct visual proportions
        // Guard: persisted pre-fix projects can carry a negative subballast (BE now clamps at source);
        // a negative layer thickness corrupts the drawing geometry, so clamp to 0 here too.
        const ballastLayer: ILayerData = { name: t('ballast', 'Ballast'), thickness: ballastThickness, color: '#4CAF50' };
        const subballastLayer: ILayerData = { name: t('subballast', 'Subballast'), thickness: Math.max(0, subballastThickness), color: '#FFC107' };
        const subgradeLayer: ILayerData = { name: t('subgrade', 'Subgrade'), thickness: subgradeVisible, color: '#8D6E63' };

        // For display: calculate total height with converted values
        const totalHeightDisplay = ballastThicknessDisplay + (subballastThicknessDisplay > 0 ? subballastThicknessDisplay : 0) + (isImperial ? subgradeVisible * mToFtFactor : subgradeVisible);

        // Check for geogrid information from project data
        const firstBaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];

        // Create geogrid info: use existing data if available, otherwise create default geogrid for Railway projects
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  positionFromBottomOfBase: firstBaseGeogrid.position ?? 0,
                  label: firstBaseGeogrid.selectedGeogrid,
              }
            : {
                  // Default geogrid for Railway projects - positioned just above the subgrade interface
                  // positionFromBottomOfBase represents distance from bottom of ballast course (subgrade interface)
                  // Use appropriate value to ensure geogrid is visibly above the ballast/subgrade boundary
                  positionFromBottomOfBase: 0.03, // Keep metric (0.03 m = ~3 cm) for consistent positioning
                  label: selectedGeogridProduct || '', // Use dynamic label from dropdown
                  color: '#24244f',
              };

        return {
            surfaceLayer: ballastLayer,
            baseCourse: subballastLayer,
            subgrade: subgradeLayer,
            rightDimensionValue: totalHeightDisplay, // Display value (converted)
            savingPercentage,
            geogrid: geogridInfo,
            unitLabel,
            sleeperThickness,
            ballastThickness: ballastThicknessDisplay, // Display value (converted)
            subballastThickness: subballastThicknessDisplay, // Display value (converted)
        };
    }, [dynamicProject.data?.type, calculatedRailwayData, backendResponseData, t, dynamicProject.data?.unit, selectedGeogridProduct]);

    // --- Choose the appropriate graphic data based on project type ---
    const finalUnreinforcedGraphicData = dynamicProject.data?.type === EProjectType.railways ? unreinforcedRailwayGraphicData : unreinforcedGraphicData;

    const finalReinforcedGraphicData = dynamicProject.data?.type === EProjectType.railways ? reinforcedRailwayGraphicData : reinforcedGraphicData;

    // --- Chart rendering logic ---

    // Render backend calculatedData table

    // === 6 NEW TABLE FUNCTIONS ===

    // 1. Not stabilised without subballast
    const renderNotStabilisedWithoutSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        // Access specific indices directly from designSummary
        const ballastThickness = backendResponseData.designSummary[0]; // Ballast thickness
        const deformation = backendResponseData.designSummary[1]; // Deformation
        const rutDepth = backendResponseData.designSummary[5]; // Rut depth

        // Convert values for Imperial display
        const ballastConverted = convertOutputValue(ballastThickness.unstabilizedValue, ballastThickness.unstabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformation.unstabilizedValue, deformation.unstabilizedUnit || 'm');
        const rutDepthConverted = convertOutputValue(rutDepth.unstabilizedValue, rutDepth.unstabilizedUnit || 'm');

        const tableData = [
            [
                ballastThickness.description || 'N/A',
                renderSymbolWithSubscript(ballastThickness.unstabilizedSymbol),
                ballastConverted.value,
                ballastConverted.unit,
            ],
            [
                deformation.description ? `${deformation.description} without subballast` : 'N/A',
                renderSymbolWithSubscript(deformation.unstabilizedSymbol),
                deformationConverted.value,
                deformationConverted.unit,
            ],
            [
                rutDepth.description || 'N/A',
                renderSymbolWithSubscript(rutDepth.unstabilizedSymbol),
                rutDepthConverted.value,
                rutDepthConverted.unit,
            ],
        ];

        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid item xs={12} md={6} style={{ paddingRight: '8px' }}>
                <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    UNSTABILISED - without subballast
                </Typography>
                <CustomTable columns={columns} data={tableData} />
            </Grid>
        );
    };

    // 2. Stabilised without subballast
    const renderStabilisedWithoutSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        // Access specific indices directly from designSummary
        const ballastThickness = backendResponseData.designSummary[0]; // Ballast thickness
        const deformation = backendResponseData.designSummary[1]; // Deformation
        const rutDepth = backendResponseData.designSummary[5]; // Rut depth

        // Convert values for Imperial display
        const ballastConverted = convertOutputValue(ballastThickness.stabilizedValue, ballastThickness.stabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformation.stabilizedValue, deformation.stabilizedUnit || 'm');
        const rutDepthConverted = convertOutputValue(rutDepth.stabilizedValue, rutDepth.stabilizedUnit || 'm');

        const tableData = [
            [
                ballastThickness.description || 'N/A',
                renderSymbolWithSubscript(ballastThickness.stabilizedSymbol),
                ballastConverted.value,
                ballastConverted.unit,
            ],
            [
                deformation.description ? `${deformation.description} without subballast` : 'N/A',
                renderSymbolWithSubscript(deformation.stabilizedSymbol),
                deformationConverted.value,
                deformationConverted.unit,
            ],
            [
                rutDepth.description || 'N/A',
                renderSymbolWithSubscript(rutDepth.stabilizedSymbol),
                rutDepthConverted.value,
                rutDepthConverted.unit,
            ],
        ];

        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid item xs={12} md={6} style={{ paddingLeft: '8px' }}>
                <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    STABILISED - without subballast
                </Typography>
                <CustomTable columns={columns} data={tableData} />
            </Grid>
        );
    };

    // 3. Not stabilised with subballast
    const renderNotStabilisedWithSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        // Access specific indices directly from designSummary
        const ballastThicknessWithSub = backendResponseData.designSummary[2]; // Ballast thickness with subballast
        const subballastThickness = backendResponseData.designSummary[3]; // Subballast thickness
        const deformationWithSub = backendResponseData.designSummary[4]; // Deformation with subballast
        const rutDepth = backendResponseData.designSummary[5]; // Rut depth

        // Convert values for Imperial display
        const ballastConverted = convertOutputValue(ballastThicknessWithSub.unstabilizedValue, ballastThicknessWithSub.unstabilizedUnit || 'm');
        const subballastConverted = convertOutputValue(subballastThickness.unstabilizedValue, subballastThickness.unstabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformationWithSub.unstabilizedValue, deformationWithSub.unstabilizedUnit || 'm');
        const rutDepthConverted = convertOutputValue(rutDepth.unstabilizedValue, rutDepth.unstabilizedUnit || 'm');

        const tableData = [
            [
                ballastThicknessWithSub.description || 'N/A',
                renderSymbolWithSubscript(ballastThicknessWithSub.unstabilizedSymbol),
                ballastConverted.value,
                ballastConverted.unit,
            ],
            [
                subballastThickness.description || 'N/A',
                renderSymbolWithSubscript(subballastThickness.unstabilizedSymbol),
                subballastConverted.value,
                subballastConverted.unit,
            ],
            [
                deformationWithSub.description || 'N/A',
                renderSymbolWithSubscript(deformationWithSub.unstabilizedSymbol),
                deformationConverted.value,
                deformationConverted.unit,
            ],
            [
                rutDepth.description || 'N/A',
                renderSymbolWithSubscript(rutDepth.unstabilizedSymbol),
                rutDepthConverted.value,
                rutDepthConverted.unit,
            ],
        ];

        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid item xs={12} md={6} style={{ paddingRight: '8px' }}>
                <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    UNSTABILISED - with subballast
                </Typography>
                <CustomTable columns={columns} data={tableData} />
            </Grid>
        );
    };

    // 4. Stabilised with subballast
    const renderStabilisedWithSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        // Access specific indices directly from designSummary
        const ballastThicknessWithSub = backendResponseData.designSummary[2]; // Ballast thickness with subballast
        const subballastThickness = backendResponseData.designSummary[3]; // Subballast thickness
        const deformationWithSub = backendResponseData.designSummary[4]; // Deformation with subballast
        const rutDepth = backendResponseData.designSummary[5]; // Rut depth

        // Convert values for Imperial display
        const ballastConverted = convertOutputValue(ballastThicknessWithSub.stabilizedValue, ballastThicknessWithSub.stabilizedUnit || 'm');
        const subballastConverted = convertOutputValue(subballastThickness.stabilizedValue, subballastThickness.stabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformationWithSub.stabilizedValue, deformationWithSub.stabilizedUnit || 'm');
        const rutDepthConverted = convertOutputValue(rutDepth.stabilizedValue, rutDepth.stabilizedUnit || 'm');

        const tableData = [
            [
                ballastThicknessWithSub.description || 'N/A',
                renderSymbolWithSubscript(ballastThicknessWithSub.stabilizedSymbol),
                ballastConverted.value,
                ballastConverted.unit,
            ],
            [
                subballastThickness.description || 'N/A',
                renderSymbolWithSubscript(subballastThickness.stabilizedSymbol),
                subballastConverted.value,
                subballastConverted.unit,
            ],
            [
                deformationWithSub.description || 'N/A',
                renderSymbolWithSubscript(deformationWithSub.stabilizedSymbol),
                deformationConverted.value,
                deformationConverted.unit,
            ],
            [
                rutDepth.description || 'N/A',
                renderSymbolWithSubscript(rutDepth.stabilizedSymbol),
                rutDepthConverted.value,
                rutDepthConverted.unit,
            ],
        ];

        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid item xs={12} md={6} style={{ paddingLeft: '8px' }}>
                <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    STABILISED - with subballast
                </Typography>
                <CustomTable columns={columns} data={tableData} />
            </Grid>
        );
    };

    // 5. Cargo train
    const renderCargoTrainTable = () => {
        if (!backendResponseData?.trainAnalysis) return null;

        // Filter for the 3 specific rows from backend: Pc, Pdyn, σsb (excluding N - Total number of passages)
        const filteredData = backendResponseData.trainAnalysis.filter(row => {
            const symbol = row.cargoSymbol || '';

            return (
                symbol === 'Pc' || // Wheel load
                symbol === 'Pdyn' || // Dynamic wheel load
                symbol === 'σsb' // Uniform pressure on ballast
            );
        });

        const tableData = filteredData.map(row => {
            // Convert values for Imperial display. Wheel load / dynamic wheel load /
            // uniform pressure on ballast are shown with 2 decimals (large kN/kPa values
            // looked like thousands with 3 trailing digits — Filippo bug2.md).
            const converted = convertOutputValue(row.cargoValue, row.cargoUnit || 'kN', 2);
            return [row.description || 'N/A', renderSymbolWithSubscript(row.cargoSymbol), converted.value, converted.unit];
        });

        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid item xs={12} md={6} style={{ paddingRight: '8px' }}>
                <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    Cargo train
                </Typography>
                <CustomTable columns={columns} data={tableData} />
            </Grid>
        );
    };

    // 6. Passenger train
    const renderPassengerTrainTable = () => {
        if (!backendResponseData?.trainAnalysis) return null;

        // Filter for the 3 specific rows from backend: Pp, Pdyn, σsb (excluding N - Total number of passages)
        const filteredData = backendResponseData.trainAnalysis.filter(row => {
            const symbol = row.passengerSymbol || '';

            return (
                symbol === 'Pp' || // Wheel load
                symbol === 'Pdyn' || // Dynamic wheel load
                symbol === 'σsb' // Uniform pressure on ballast
            );
        });

        const tableData = filteredData.map(row => {
            // Convert values for Imperial display. 2 decimals for the train-analysis
            // values (see cargo table note above).
            const converted = convertOutputValue(row.passengerValue, row.passengerUnit || 'kN', 2);
            return [row.description || 'N/A', renderSymbolWithSubscript(row.passengerSymbol), converted.value, converted.unit];
        });

        const columns = [t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')];

        return (
            <Grid item xs={12} md={6} style={{ paddingLeft: '8px' }}>
                <Typography variant="h6" component="strong" style={{ color: '#004652', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    Passenger train
                </Typography>
                <CustomTable columns={columns} data={tableData} />
            </Grid>
        );
    };

    // Render calculatedAt and debugInfo

    return (
        <Grid container id="results-component-container">
            {/* --- Renderizza i DUE diagrammi PRIMA delle tabelle input --- */}
            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography align="center" variant="subtitle1" gutterBottom style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    {t('unreinforced_diagram_title', 'Unreinforced')}
                </Typography>
                {/* Aggiunto flexGrow: 1 al div contenitore del diagramma */}
                <div
                    id="tire-layer-unreinforced"
                    className="cont-grafico"
                    style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', padding: '10px', minHeight: '300px' }}>
                    {finalUnreinforcedGraphicData ? (
                        <TireAndLayers
                            // Dati layer specifici per Unreinforced
                            surfaceLayer={finalUnreinforcedGraphicData.surfaceLayer}
                            baseCourse={finalUnreinforcedGraphicData.baseCourse}
                            subgrade={finalUnreinforcedGraphicData.subgrade}
                            // Mostra solo quota destra
                            rightDimensionValue={finalUnreinforcedGraphicData.rightDimensionValue} // Pass right value
                            showLeftDimension={false} // Hide left dimension
                            showRightDimension={true} // Show right dimension
                            // Nascondi gli altri elementi specifici del rinforzato
                            showSavingBox={false}
                            showGeogrid={false}
                            showTire={true} // Show tire/container
                            showLayers={true} // Show layers
                            // leftDimensionStrokeWidth={2.5} // Remove as left dimension is hidden
                            unitLabel={finalUnreinforcedGraphicData.unitLabel}
                            projectType={currentProjectType} // Pass projectType
                            // Railway-specific layer thickness data
                            showLayerThickness={currentProjectType === EProjectType.railways}
                            sleeperThickness={(finalUnreinforcedGraphicData as IRailwayGraphicData).sleeperThickness}
                            ballastThickness={(finalUnreinforcedGraphicData as IRailwayGraphicData).ballastThickness}
                            subballastThickness={(finalUnreinforcedGraphicData as IRailwayGraphicData).subballastThickness}
                            onStageHeightCalculated={(h: number) => {
                                setUnreinforcedStageHeight(h);
                            }}
                            onSubgradeYCalculated={(y: number) => {
                                setUnreinforcedSubgradeY(y);
                            }}
                        />
                    ) : (
                        <Typography>Loading diagram...</Typography>
                    )}
                </div>
            </Grid>

            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography align="center" variant="subtitle1" gutterBottom style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    {t('reinforced_diagram_title', 'Reinforced')}
                </Typography>
                {/* Aggiunto flexGrow: 1 al div contenitore del diagramma */}
                <div
                    id="tire-layer-reinforced"
                    className="cont-grafico"
                    style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', padding: '10px', minHeight: '300px' }}>
                    {finalReinforcedGraphicData ? (
                        <TireAndLayers
                            // Dati layer specifici per Reinforced
                            surfaceLayer={finalReinforcedGraphicData.surfaceLayer}
                            baseCourse={finalReinforcedGraphicData.baseCourse}
                            subgrade={finalReinforcedGraphicData.subgrade}
                            // Mostra quota destra, saving, geogriglia, pneumatico
                            rightDimensionValue={finalReinforcedGraphicData.rightDimensionValue}
                            savingPercentage={finalReinforcedGraphicData.savingPercentage}
                            geogrid={finalReinforcedGraphicData.geogrid}
                            showRightDimension={true} // Ensure right is shown
                            showSavingBox={true}
                            showGeogrid={true}
                            showTire={true} // Show tire (default)
                            // showLayers={true} // Show layers (default, no need to specify)
                            // Nascondi quota sinistra
                            showLeftDimension={false} // Ensure left is hidden
                            unitLabel={finalReinforcedGraphicData.unitLabel}
                            customStageHeight={unreinforcedStageHeight}
                            alignFromBottom={true}
                            targetSubgradeY={unreinforcedSubgradeY}
                            hideLayerTitles={false}
                            projectType={currentProjectType} // Pass projectType
                            // Railway-specific layer thickness data
                            showLayerThickness={currentProjectType === EProjectType.railways}
                            sleeperThickness={(finalReinforcedGraphicData as IRailwayGraphicData).sleeperThickness}
                            ballastThickness={(finalReinforcedGraphicData as IRailwayGraphicData).ballastThickness}
                            subballastThickness={(finalReinforcedGraphicData as IRailwayGraphicData).subballastThickness}
                            setStabilisedBallastThickness={setStabilisedBallastThickness}
                        />
                    ) : (
                        <Typography>Loading diagram...</Typography>
                    )}
                </div>
            </Grid>

            {/* TABELLE INPUT - DOPO i canvas */}
            {dynamicProject.data?.type === EProjectType.railways &&
                (() => {
                    return (
                        <Grid item xs={12} style={{ marginTop: '24px' }}>
                            {/* Input Parameters Title */}
                            <Typography
                                variant="h5"
                                component="h2"
                                style={{
                                    color: '#004652',
                                    fontWeight: 'bold',
                                    marginBottom: '16px',
                                    textAlign: 'center',
                                }}>
                                Results
                            </Typography>

                            {/* Cargo Train Table - shown for cargo_train or mixed_train */}
                            {(railwayToDesign === 'cargo_train' || railwayToDesign === 'mixed_train') &&
                                renderInputTable(cargoTrainInputs, t('cargo_train') || 'Cargo train', 'cargo-train')}

                            {/* Passenger Train Table - shown for passenger_train or mixed_train */}
                            {(railwayToDesign === 'passenger_train' || railwayToDesign === 'mixed_train') &&
                                renderInputTable(passengerTrainInputs, t('passenger_train') || 'Passenger train', 'passenger-train')}

                            {/* Railway Geometry Table - always shown */}
                            {renderInputTable(railwayGeometryInputs, t('railway_geometry') || 'Railway geometry', 'railway-geometry')}

                            {/* Soil Properties Table - always shown */}
                            {renderInputTable(soilPropertiesInputs, t('soil_properties') || 'Soil properties', 'soil-properties')}
                        </Grid>
                    );
                })()}

            {/* TABELLE RESULTS - DOPO i canvas - 6 tabelle dinamiche */}
            {dynamicProject.data?.type === EProjectType.railways &&
                backendResponseData &&
                (() => {
                    const hasSubballast = setStabilisedBallastThickness > 0;
                    const railwayToDesign = railwayProjectInputs?.railwayType?.railwayToDesign;

                    return (
                        <>
                            {/* BALLAST DESIGN TABLES (2x2 grid based on hasSubballast) */}
                            <Grid container spacing={2} style={{ marginTop: '16px' }}>
                                {hasSubballast ? (
                                    // WITH SUBBALLAST: Show "Not stabilised with subballast" + "Stabilised with subballast"
                                    <>
                                        {renderNotStabilisedWithSubballast()}
                                        {renderStabilisedWithSubballast()}
                                    </>
                                ) : (
                                    // WITHOUT SUBBALLAST: Show "Not stabilised without ballast" + "Stabilised without subballast"
                                    <>
                                        {renderNotStabilisedWithoutSubballast()}
                                        {renderStabilisedWithoutSubballast()}
                                    </>
                                )}
                            </Grid>

                            {/* TRAIN ANALYSIS TABLES (1x2 grid based on railwayToDesign) */}
                            <Grid container spacing={2} style={{ marginTop: '16px' }}>
                                {railwayToDesign === 'cargo_train' &&
                                    // Show only Cargo train
                                    renderCargoTrainTable()}
                                {railwayToDesign === 'passenger_train' &&
                                    // Show only Passenger train
                                    renderPassengerTrainTable()}
                                {railwayToDesign === 'mixed_train' && (
                                    // Show both Cargo train + Passenger train
                                    <>
                                        {renderCargoTrainTable()}
                                        {renderPassengerTrainTable()}
                                    </>
                                )}
                            </Grid>
                        </>
                    );
                })()}
        </Grid>
    );
};

export default Results;
